import { Event } from '@yandex-int/messenger.utils';
import { buffers, channel, Channel } from 'redux-saga';
import { call, delay, race, take } from 'typed-redux-saga';

import { ChannelHolder } from '../../../../helpers/channelHolder';
import { createLogger } from '../../../../helpers/loggerFacade';
import { BaseState } from './base-state';
import { CommandListState } from './command-list-state';
import { DebugMenuState } from './debug-menu-state';
import { IdleState } from './idle-state';
import { ParticipantListState } from './participant-list-state';
import { StateId } from './types';

const logger = createLogger('dtmf-state-machine');

type DTMFStateClassType = typeof BaseState;

const DTMF_STATE_CONSTRUCTORS: Record<StateId, DTMFStateClassType> = {
    idle: IdleState,
    'command-list': CommandListState,
    'participant-list': ParticipantListState,
    'debug-menu': DebugMenuState,
};

const GLOBAL_RESET_DELAY = 60000;
const GLOBAL_RESET_DISABLED_STATES = new Set<StateId | null>(['idle', 'debug-menu']);

export class DTMFStateMachine {
    private state: BaseState;
    private requestTransitionChannel: Channel<StateId>;

    constructor() {
        this.requestTransitionChannel = channel<StateId>(buffers.sliding(1));

        this.state = new BaseState({ requestTransitionChannel: this.requestTransitionChannel });
    }

    private *transition(NextState: DTMFStateClassType) {
        yield* call([this.state, this.state.onExit]);

        this.state = new NextState({ requestTransitionChannel: this.requestTransitionChannel });

        yield* call([this.state, this.state.onEnter]);
    }

    public *start(dtmfEventChannel: Event<string>) {
        yield* call([this, this.transition], DTMF_STATE_CONSTRUCTORS.idle);

        const holder = new ChannelHolder();

        try {
            while (true) {
                // Не делаем delay, если в текущем состоянии нет смысла делать reset, чтобы лишний раз не крутить цикл.
                const globalResetEffect = (() => {
                    if (GLOBAL_RESET_DISABLED_STATES.has(this.state.id)) {
                        return null;
                    }

                    return delay(GLOBAL_RESET_DELAY);
                })();
                const raceEffects = [
                    holder.take(dtmfEventChannel),
                    take(this.requestTransitionChannel),
                    globalResetEffect,
                ].filter(Boolean);

                const [dtmfChar, transitionRequest, globalReset] = yield* race(raceEffects);

                if (globalReset) {
                    yield* call([this, this.transition], IdleState);
                }

                if (transitionRequest) {
                    yield* call([this, this.transition], DTMF_STATE_CONSTRUCTORS[transitionRequest as StateId]);
                }

                if (typeof dtmfChar === 'string') {
                    logger.debug('DTMF char:', dtmfChar);

                    yield* call([this.state, this.state.process], dtmfChar);
                }
            }
        } finally {
            holder.close();
        }
    }
}
